import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useMutation, useQuery } from '@apollo/client'
import { GOOGLE_MEET_CONFIGURATION } from '../../../graphql/mutation'
import { toast } from 'react-toastify'
import {
  GET_ALL_EXTERNAL_SERVICES,
  GET_GOOGLE_MEET_CONFIG_BY_BG_ACCOUNT
} from '../../../graphql/query'
import { usePlan } from '../../../context/PlanContext'

interface IProps {
  closeGoogleMeetModal: () => void
}
const GoogleMeetModal: React.FC<IProps> = ({ closeGoogleMeetModal }) => {
  const { currentPlan } = usePlan()
  const [initialValues, setInitialValues] = useState<any>({
    clientId: '',
    clientSecret: '',
    googleCalendarId: '',
    externalServiceId: null,
    redirectUrl: ''
  })

  const {
    data: GoogleMeetData,
    refetch,
    loading,
    error
  } = useQuery(GET_GOOGLE_MEET_CONFIG_BY_BG_ACCOUNT, {
    variables: {
      input: {
        bgAccountId: currentPlan?.bgAccountId
      }
    }
  })

  const [saveGoogleMeetConfiguration] = useMutation(GOOGLE_MEET_CONFIGURATION, {
    context: { headers: { 'access-control-allow-methods': 'Configuration' } }
  })

  const { data } = useQuery(GET_ALL_EXTERNAL_SERVICES)

  const googleMeetService = data?.getAllExternalServices?.find(
    (service: any) => service.name.toLowerCase() === 'googlemeet'
  )
  const googleMeetServiceId = googleMeetService ? googleMeetService.id : 0

  // Validation Schema
  const validationSchema = Yup.object({
    clientId: Yup.string().required('Client id is required'),
    clientSecret: Yup.string().required('Client secret id is required'),
    googleCalendarId: Yup.string().required('Google calendar id is required'),
    redirectUrl: Yup.string()
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Redirect url is required')
  })

  useEffect(() => {
    if (GoogleMeetData) {
      setInitialValues({
        clientId: GoogleMeetData?.getGoogleMeetConfigByBgAccountI?.[0]?.clientId || '',
        clientSecret: GoogleMeetData?.getGoogleMeetConfigByBgAccountI?.[0]?.clientSecret || '',
        googleCalendarId:
          GoogleMeetData?.getGoogleMeetConfigByBgAccountI?.[0]?.googleCalendarId || '',
        externalServiceId:
          GoogleMeetData?.getGoogleMeetConfigByBgAccountI?.[0]?.externalServiceId ||
          googleMeetServiceId,
        redirectUrl: GoogleMeetData?.getGoogleMeetConfigByBgAccountI?.[0]?.redirectUrl || ''
      })
    }
  }, [GoogleMeetData, googleMeetServiceId])

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      saveGoogleMeetConfiguration({
        variables: {
          input: {
            externalServiceId: googleMeetServiceId,
            clientId: values.clientId,
            clientSecret: values.clientSecret,
            tenantId: 'null',
            googleCalendarId: values.googleCalendarId,
            redirectUrl: values.redirectUrl
          }
        }
      }).then((res: any) => {
        closeGoogleMeetModal()
        if (
          res.data.saveGoogleMeetConfiguration.status >= 200 &&
          res.data.saveGoogleMeetConfiguration.status < 300
        ) {
          toast.success(res.data.saveGoogleMeetConfiguration.message)
        } else {
          toast.error(res.data.saveGoogleMeetConfiguration.message)
        }
      })
      refetch().catch((err) => {
        closeGoogleMeetModal()
        toast.error('Sorry! Unexpected Error.')
        console.error(err)
      })
    }
  })

  if (loading) {
    return <>Loading...</>
  }

  if (error) {
    console.log(error)
  }

  return (
    <div className='collapse show'>
      <form className='form' onSubmit={formik.handleSubmit}>
        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}
        <div className='d-flex justify-content-between align-items-center px-3 py-2'>
          <h2 className='mb-0'>Google Meet Configuration</h2>
        </div>

        <div className='d-flex justify-content-between align-items-center px-3 py-2'>
          <p className='mb-0'>Please fill out all the required fields below.</p>
        </div>
        <div className='card-body p-9'>
          <div className='row mb-3'>
            <label className='col-form-label required fw-semibold fs-6'>Client ID</label>

            <div className='fv-row'>
              <input
                type='text'
                {...formik.getFieldProps('clientId')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.clientId && formik.errors.clientId
                  },
                  {
                    'is-valid': formik.touched.clientId && !formik.errors.clientId
                  }
                )}
                placeholder='Client Id'
              />
              {formik.touched.clientId && formik.errors.clientId && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert text-danger'>{String(formik.errors.clientId)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-3'>
            <label className='col-form-label required fw-semibold fs-6'>Client Secret Id</label>

            <div className='fv-row'>
              <input
                type='text'
                {...formik.getFieldProps('clientSecret')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.clientSecret && formik.errors.clientSecret
                  },
                  {
                    'is-valid': formik.touched.clientSecret && !formik.errors.clientSecret
                  }
                )}
                placeholder='Client secret Id'
              />
              {formik.touched.clientSecret && formik.errors.clientSecret && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{String(formik.errors.clientSecret)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-3'>
            <label className='col-form-label required fw-semibold fs-6'>Google Calendar Id</label>

            <div className='fv-row'>
              <input
                type='text'
                {...formik.getFieldProps('googleCalendarId')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.googleCalendarId && formik.errors.googleCalendarId
                  },
                  {
                    'is-valid': formik.touched.googleCalendarId && !formik.errors.googleCalendarId
                  }
                )}
                placeholder='Google Calendar Id'
              />
              {formik.touched.googleCalendarId && formik.errors.googleCalendarId && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{String(formik.errors.googleCalendarId)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='row mb-3'>
            <label className='col-form-label required fw-semibold fs-6'>Redirect Url</label>

            <div className='fv-row'>
              <input
                type='text'
                {...formik.getFieldProps('redirectUrl')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.redirectUrl && formik.errors.redirectUrl
                  },
                  {
                    'is-valid': formik.touched.redirectUrl && !formik.errors.redirectUrl
                  }
                )}
                placeholder='redirectUrl Url'
              />
              {formik.touched.redirectUrl && formik.errors.redirectUrl && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{String(formik.errors.redirectUrl)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='d-flex justify-content-end mt-4'>
            <button type='button' className='btn btn-secondary me-4' onClick={closeGoogleMeetModal}>
              Cancel
            </button>
            <button type='submit' className='btn btn-sm btn-primary' disabled={formik.isSubmitting}>
              Save
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default GoogleMeetModal
